<script setup lang="ts">
import {computed, defineEmits, defineProps, ref, watch, withDefaults} from "vue";
import SidebarNavigation from "./sidebarNavigation.vue";

const props = withDefaults(
    defineProps<{
      filter: any;
      activeValue: any;
      mode?: "normal" | "small";
    }>(),
    {
      filter: () => ({}),
      activeValue: "",
      mode: "normal",
    }
);

const emit = defineEmits<{
  (e: "update", value: string): void;
}>();

const itemKeys = computed(() => Object.keys(props.filter.values || {}));
const itemRefs = ref<HTMLElement[]>([]);
const scrollContainer = ref<HTMLElement | null>(null);
const manualIndex = ref(0);

const currentIndex = computed({
  get: () => manualIndex.value,
  set: (val) => {
    manualIndex.value = val;
    scrollToIndex(val);
  }
});

const scrollToIndex = (index: number) => {
  const el = itemRefs.value[index];
  if (el) {
    el.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'center'});
  }
};

const onScroll = () => {
  if (!scrollContainer.value) return;
  const container = scrollContainer.value;
  const containerCenter = container.scrollLeft + container.clientWidth / 2;

  let closestIndex = 0;
  let minDistance = Infinity;

  itemRefs.value.forEach((el, index) => {
    if (!el) return;
    const elCenter = el.offsetLeft + el.clientWidth / 2;
    const distance = Math.abs(elCenter - containerCenter);
    if (distance < minDistance) {
      minDistance = distance;
      closestIndex = index;
    }
  });

  manualIndex.value = closestIndex;
};

watch(() => props.activeValue, (newVal) => {
  const idx = itemKeys.value.indexOf(newVal?.toString() || "");
  if (idx >= 0) {
    manualIndex.value = idx;
  }
}, {immediate: true});

const getImageUrl = (value: string) => {
  if (!props.filter.image) return "";
  const processedVal = props.filter.lowercaseImage !== false ? value.toLowerCase() : value;
  return props.filter.image.replaceAll("{value}", processedVal);
};

const updateValue = (val: string) => {
  emit("update", val);
};
</script>

<template>
  <div class="flex flex-col">
    <div
        ref="scrollContainer"
        class="overflow-x-auto no-scrollbar scroll-smooth"
        @scroll="onScroll"
    >
      <div
          class="flex gap-8 px-2 items-center"
      >
        <div
            v-for="(label, val, index) in filter.values"
            :key="val"
            :ref="el => { if (el) itemRefs[index] = el as any }"
            class="flex flex-col justify-center items-center cursor-pointer min-w-fit"
            @click="updateValue(val.toString())"
        >
          <div
              class="overflow-hidden rounded-full flex items-center justify-center mb-2 transition-all duration-300"
              :class="[
                mode === 'small' ? 'w-8 h-8' : 'w-16 h-16',
                activeValue === val ? (mode === 'small' ? 'shadow-md w-10 h-10 border border-brand-main border-opacity-50' : 'shadow-md w-20 h-20 border border-brand-main') : ''
            ]"
          >
            <img
                v-if="val.toString() !== ''"

                :src="getImageUrl(val.toString())"
                :alt="label"
                class="w-full h-full object-contain"
                :style="{ transform: `scale(${0.4 + (val / 3) * 0.6})` }"
                @error="(e: any) => e.target.style.display = 'none'"
            />
            <div v-else class="w-full h-full flex items-center justify-center"
                 :class="mode === 'small' ? 'text-xs' : ''">
              {{ label }}
            </div>
          </div>
          <span
              v-if="val.toString() !== ''"
              class="text-gray-500 tracking-wide whitespace-nowrap"
              :class="[
                activeValue === val ? 'font-bold' : '',
                mode === 'small' ? 'text-xs' : 'text-sm'
            ]"
          >{{ label }}</span>
        </div>
      </div>
    </div>
    <sidebar-navigation
        :total="itemKeys.length"
        v-model="currentIndex"
        :container="scrollContainer"
    />
  </div>
</template>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>